import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToolbarItem, useToast } from '@kinesis/bungle';
import { useIsEditor } from '@/hooks/use-is-editor';
import { useInventoryId } from '@/hooks/use-inventory-id';
import { useNewDisclosureMutation } from '@/api/disclosures';
import { NewDisclosurePopover } from '@/components/new-disclosure-popover';
import { disclosurePath } from '@/links';
import { DisclosureDefinition } from '@/types';

// Per-route toolbar injection for the Disclosures section. Mirrors
// `toolbar-pathways.tsx` (buttons in the inventory toolbar, editor-gated) and
// suit's `toolbar-disclosures`: ONE "New disclosure" button that opens a
// searchable type picker (`NewDisclosurePopover`) — the type is chosen there,
// not by a button-per-type. New disclosure types appear in the picker by
// extending `data/disclosures` + the `DisclosureType` union; this file does not
// change. The created draft's name/period are configured on the detail page.
const ToolbarDisclosures = () => {
  const [newDisclosure] = useNewDisclosureMutation();
  const navigate = useNavigate();
  const inventoryId = useInventoryId();
  const toast = useToast('globalTop');
  const isEditor = useIsEditor();

  const onCreate = useCallback(
    async (definition: DisclosureDefinition) => {
      try {
        const data = await newDisclosure({
          inventoryId,
          name: 'New disclosure',
          definition,
        }).unwrap();
        navigate(disclosurePath(inventoryId, data.id));
      } catch {
        toast('Could not create disclosure.', { variant: 'error' });
      }
    },
    [newDisclosure, inventoryId, navigate, toast],
  );

  return isEditor ? (
    <ToolbarItem>
      <NewDisclosurePopover onCreate={onCreate} />
    </ToolbarItem>
  ) : null;
};

export { ToolbarDisclosures };
